/**
 * The aggregation a column's footer shows when nobody has chosen one: the type's declared
 * `defaultAggregation`, else the head of its menu. Both the SQL path (`apps/api`'s
 * `loadSummaries`) and the browser path read it here, so they cannot disagree (P-027).
 */
export function defaultAggregationOf(definition) {
    return definition.defaultAggregation ?? definition.aggregations[0] ?? 'none';
}
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** The aggregations whose answer is a moment in time, whatever the column type is. */
const DATE_AGGREGATIONS = new Set(['earliest', 'latest', 'range']);
export function isDateAggregation(aggregation) {
    return DATE_AGGREGATIONS.has(aggregation);
}
/**
 * Accepts everything the two paths can hand over: a `Date`, an ISO string, and PostgreSQL's
 * `2026-04-15 00:00:00+00` — which `new Date()` rejects outright, on two counts: the space
 * where ISO wants a `T`, and the two-digit offset where ISO wants `+00:00`. Both are
 * normalised here. Anything else is not a date and is reported as such.
 */
export function parseSummaryDate(value) {
    if (value === null || value === undefined)
        return null;
    if (value instanceof Date)
        return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'number') {
        const fromNumber = new Date(value);
        return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
    }
    if (typeof value !== 'string')
        return null;
    const text = value.trim();
    if (text === '')
        return null;
    const withT = /^\d{4}-\d{2}-\d{2} /.test(text) ? text.replace(' ', 'T') : text;
    // `…T00:00:00+00` -> `…T00:00:00+00:00`. Anchored on the time so a bare `2026-01-15` (whose
    // last three characters also look like an offset) is left exactly as it is.
    const normalised = withT.replace(/(\d{2}:\d{2}(?::\d{2})?)([+-]\d{2})$/, '$1$2:00');
    const parsed = new Date(normalised);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}
/** monday's short board date, in UTC (the stored value is UTC, like monday's own). */
export function formatSummaryDate(value, now = new Date()) {
    const date = parseSummaryDate(value);
    if (date === null)
        return null;
    const year = date.getUTCFullYear();
    const head = `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
    return year === now.getUTCFullYear() ? head : `${head}, ${year}`;
}
export function formatSummaryNumber(value) {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
}
/**
 * One scalar aggregate as text. `''` when there is nothing to show — an empty cell is monday's
 * default, and never an ISO timestamp.
 */
export function formatSummaryScalar(aggregation, value, now = new Date()) {
    if (value === null || value === undefined)
        return '';
    // A moment is rendered as a date whatever the aggregation asked for — `String(new Date())`
    // is a timestamp too, just a friendlier-looking one.
    if (isDateAggregation(aggregation) || value instanceof Date)
        return formatSummaryDate(value, now) ?? '';
    if (typeof value === 'number')
        return formatSummaryNumber(value);
    if (typeof value === 'string') {
        const asNumber = Number(value);
        if (value.trim() !== '' && Number.isFinite(asNumber))
            return formatSummaryNumber(asNumber);
        // A stray timestamp on a non-date aggregation is still never printed raw.
        return formatSummaryDate(value, now) ?? value;
    }
    return String(value);
}
/** `range`'s two ends, joined the way the footer shows them. */
export function formatSummaryPair(aggregation, from, to, now = new Date()) {
    const left = formatSummaryScalar(aggregation, from, now);
    const right = formatSummaryScalar(aggregation, to, now);
    if (left === '' && right === '')
        return '';
    return `${left} – ${right}`;
}
//# sourceMappingURL=summary-format.js.map